import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Text from "../../../components/atoms/Text";
import Button from "../../../components/atoms/Button";
import Tag from "../../../components/atoms/Tag";
import Image from "../../../components/atoms/Image";
import { DrugSearchResult, DrugSearchCondition, DrugIdentificationStatus } from "../../../common/models/types";
import {
  DrugIdentificationWithUser,
  getDrugIdentificationImageUrl,
  fetchDrugIdentificationById,
  updateDrugIdentificationCondition,
  DrugIdentificationUpdateParams
} from "../../../api/drugIdentificationService";
import DrugSearchConditionForm, {
  DrugSearchConditionState,
  toInputValue,
  parseIdentifyTextInput
} from "../../../components/organisms/DrugSearchConditionForm";
import DrugSearchResultList from "../../../components/organisms/DrugSearchResultList";
import { useParams } from "react-router-dom";

// 상태별 태그 색상
const getStatusTagColor = (status: DrugIdentificationStatus): "Green" | "Orange" | "Red" | "Blue" | "Gray" => {
  switch (status) {
    case DrugIdentificationStatus.COMPLETED:
      return "Green";
    case DrugIdentificationStatus.SELECTED:
      return "Blue";
    case DrugIdentificationStatus.PROCESSING:
      return "Orange";
    case DrugIdentificationStatus.PENDING:
      return "Gray";
    case DrugIdentificationStatus.FAILED:
    case DrugIdentificationStatus.WRONG:
      return "Red";
    default:
      return "Gray";
  }
};

// 상태 텍스트
const getStatusText = (status: DrugIdentificationStatus): string => {
  switch (status) {
    case DrugIdentificationStatus.COMPLETED: return '완료';
    case DrugIdentificationStatus.PROCESSING: return '처리중';
    case DrugIdentificationStatus.FAILED: return '실패';
    case DrugIdentificationStatus.PENDING: return '대기';
    case DrugIdentificationStatus.SELECTED: return '확정';
    case DrugIdentificationStatus.WRONG: return '오류';
    default: return status;
  }
};

const Container = styled.div`
  padding: 24px;
  background: #f9fafb;
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled(Text)`
  font-size: 20px;
  font-weight: 600;
  color: #333;
`;

const HeaderInfo = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

// 이미지 섹션 (상단)
const ImageSection = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
`;

const ImageContainer = styled.div`
  display: flex;
  gap: 24px;
  justify-content: center;
`;

const UploadedImage = styled.div`
  max-width: 300px;
  max-height: 300px;

  img {
    border-radius: 8px;
    border: 1px solid #e5e7eb;
  }
`;

// 이력 네비게이션
const HistoryNavigation = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const NavButton = styled(Button)`
  min-width: 80px;
`;

const HistoryInfo = styled.div`
  text-align: center;
`;

const HistoryLabel = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const HistoryDate = styled(Text)`
  font-size: 12px;
  color: #6b7280;
`;

// 검색 조건 + 결과 (하단 2단)
const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 24px;
`;

interface DrugIdentificationDetailPopupProps {
  identificationId?: number;
}

const DrugIdentificationDetailPopup: React.FC<DrugIdentificationDetailPopupProps> = ({ identificationId }) => {
  const { id: paramId } = useParams<{ id: string }>();
  const id = identificationId ? String(identificationId) : paramId;
  const [identification, setIdentification] = useState<DrugIdentificationWithUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1); // -1: 현재(최종) 결과

  // 수정 모드 상태
  const [isEditMode, setIsEditMode] = useState(false);
  const [searching, setSearching] = useState(false);
  const [editCondition, setEditCondition] = useState<DrugSearchConditionState>({
    identifyText1: '',
    identifyText2: '',
    ignoreText1: false,
    ignoreText2: false,
    formulation: [],
    shape: [],
    color: [],
    line: [],
    limit: 10
  });

  const loadDetail = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await fetchDrugIdentificationById(parseInt(id));
      setIdentification(response.data);
    } catch (error) {
      console.error("Failed to load detail:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDetail();
  }, [id]);

  // 수정 모드 진입 시 현재 조건으로 초기화
  const handleEnterEditMode = () => {
    if (!identification) return;

    const currentCondition = identification.conditions || {};
    setEditCondition({
      identifyText1: toInputValue(currentCondition.identifyText1),
      identifyText2: toInputValue(currentCondition.identifyText2),
      ignoreText1: false,
      ignoreText2: false,
      formulation: currentCondition.formulation || [],
      shape: currentCondition.shape || [],
      color: currentCondition.color || [],
      line: currentCondition.line || [],
      limit: 10
    });
    setIsEditMode(true);
  };

  // 수정 취소
  const handleCancelEdit = () => {
    setIsEditMode(false);
  };

  // 검색 실행
  const handleSearch = async () => {
    if (!id) return;

    try {
      setSearching(true);

      const params: DrugIdentificationUpdateParams = {
        identifyText1: editCondition.ignoreText1 ? null : parseIdentifyTextInput(editCondition.identifyText1),
        identifyText2: editCondition.ignoreText2 ? null : parseIdentifyTextInput(editCondition.identifyText2),
        formulation: editCondition.formulation,
        shape: editCondition.shape,
        color: editCondition.color,
        line: editCondition.line,
        limit: editCondition.limit
      };

      await updateDrugIdentificationCondition(parseInt(id), params);

      // 데이터 새로 로드
      await loadDetail();

      // 최신 결과로 이동
      setCurrentHistoryIndex(-1);
      setIsEditMode(false);
    } catch (error) {
      console.error("Failed to update condition:", error);
      alert("검색 조건 수정에 실패했습니다.");
    } finally {
      setSearching(false);
    }
  };

  if (loading || !identification) {
    return (
      <Container>
        <Text>로딩 중...</Text>
      </Container>
    );
  }

  const { conditions, results, selectedItemCode, historys } = identification;

  // 이력 데이터 구성: historys 배열 + 현재(최종) 결과
  const totalHistoryCount = (historys?.length || 0) + 1;
  const hasHistory = totalHistoryCount > 1;

  // 현재 표시할 조건과 결과
  const getCurrentData = (): { condition: DrugSearchCondition; result: DrugSearchResult[]; label: string; date?: string } => {
    if (currentHistoryIndex === -1 || !historys) {
      return {
        condition: conditions || {},
        result: results || [],
        label: hasHistory ? `최종 결과 (${totalHistoryCount}/${totalHistoryCount})` : '검색 결과',
        date: identification.updatedAt ? new Date(identification.updatedAt).toLocaleString('ko-KR') : undefined
      };
    }

    const history = historys[currentHistoryIndex];
    return {
      condition: history.condition,
      result: history.result,
      label: currentHistoryIndex === 0
        ? `최초 AI 추출 (1/${totalHistoryCount})`
        : `수정 ${currentHistoryIndex} (${currentHistoryIndex + 1}/${totalHistoryCount})`,
      date: history.historyAt ? new Date(history.historyAt).toLocaleString('ko-KR') : undefined
    };
  };

  const currentData = getCurrentData();

  // 이전/다음 이력으로 이동
  const handlePrevHistory = () => {
    if (currentHistoryIndex === -1 && historys) {
      setCurrentHistoryIndex(historys.length - 1);
    } else if (currentHistoryIndex > 0) {
      setCurrentHistoryIndex(currentHistoryIndex - 1);
    }
  };

  const handleNextHistory = () => {
    if (historys && currentHistoryIndex < historys.length - 1) {
      setCurrentHistoryIndex(currentHistoryIndex + 1);
    } else if (currentHistoryIndex === historys!.length - 1) {
      setCurrentHistoryIndex(-1);
    }
  };

  const canGoPrev = currentHistoryIndex > 0 || (currentHistoryIndex === -1 && hasHistory);
  const canGoNext = currentHistoryIndex >= 0;

  // 히스토리 태그 타입 결정
  const getHistoryTagType = (): 'ai' | 'user' | 'none' => {
    if (currentHistoryIndex === 0) return 'ai';
    if (currentHistoryIndex > 0) return 'user';
    return 'none';
  };

  return (
    <Container>
      <Header>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Title>의약품 식별 상세 #{identification.id}</Title>
          <Tag variant="Tinted" color={getStatusTagColor(identification.status)} size="Small">
            {getStatusText(identification.status)}
          </Tag>
        </div>
        <HeaderInfo>
          <Text textType="Caption" color="#6b7280">
            {identification.createdAt && new Date(identification.createdAt).toLocaleString('ko-KR')}
          </Text>
        </HeaderInfo>
      </Header>

      {/* 이미지 섹션 */}
      <ImageSection>
        <ImageContainer>
          <UploadedImage>
            <Image
              src={getDrugIdentificationImageUrl(parseInt(id!), 0)}
              alt="업로드 이미지 1"
              width={300}
              height={300}
              objectFit="contain"
              enableHoverZoom={true}
              zoomScale={2}
              showOnError={false}
            />
          </UploadedImage>
          <UploadedImage>
            <Image
              src={getDrugIdentificationImageUrl(parseInt(id!), 1)}
              alt="업로드 이미지 2"
              width={300}
              height={300}
              objectFit="contain"
              enableHoverZoom={true}
              zoomScale={2}
              showOnError={false}
            />
          </UploadedImage>
        </ImageContainer>
      </ImageSection>

      {/* 이력 네비게이션 (이력이 있을 때만 표시) */}
      {hasHistory && (
        <HistoryNavigation>
          <NavButton
            size="Small"
            type="Text"
            variant="Outlined"
            color="Secondary"
            onClick={handlePrevHistory}
            disabled={!canGoPrev}
          >
            ← 이전
          </NavButton>
          <HistoryInfo>
            <HistoryLabel>{currentData.label}</HistoryLabel>
            {currentData.date && (
              <HistoryDate>{currentData.date}</HistoryDate>
            )}
          </HistoryInfo>
          <NavButton
            size="Small"
            type="Text"
            variant="Outlined"
            color="Secondary"
            onClick={handleNextHistory}
            disabled={!canGoNext}
          >
            다음 →
          </NavButton>
        </HistoryNavigation>
      )}

      {/* 검색 조건 + 결과 (2단 레이아웃) */}
      <ContentGrid>
        {/* 검색 조건 섹션 */}
        <DrugSearchConditionForm
          condition={editCondition}
          onChange={setEditCondition}
          onSearch={handleSearch}
          onCancel={handleCancelEdit}
          searching={searching}
          alwaysEditMode={false}
          showCancelButton={true}
          isEditMode={isEditMode}
          onEnterEditMode={currentHistoryIndex === -1 ? handleEnterEditMode : undefined}
          readOnlyCondition={currentData.condition}
          historyTag={{ type: getHistoryTagType() }}
        />

        {/* 검색 결과 섹션 */}
        <DrugSearchResultList
          results={currentData.result}
          selectedItemCode={currentHistoryIndex === -1 ? selectedItemCode : undefined}
          isConfirmed={identification.status === DrugIdentificationStatus.SELECTED}
          maxHeight="600px"
        />
      </ContentGrid>
    </Container>
  );
};

export default DrugIdentificationDetailPopup;
